// SATELLITE-REGISTRY in the in-memory API: Operator › Platform › Satellites
// (services/api/src/routes/operator-satellites.ts) with the real routes' main rules —
// - who: an agency ADMIN whose agency holds an operator grant on HTVA (the platform company);
//   another agency user is 403, anyone else 404;
// - an address is an origin (WireGuard: http(s) inside 100.64.0.0/10; mTLS: https to a public
//   name); an agent's gateway URL is on its satellite's host;
// - one active endpoint per agent: a new one replaces it; a gateway key is dropped on arrival
//   (the real API seals it) and never appears in any answer;
// - "Check now" never reaches a network: a WireGuard host ending in `.99` does not answer (two
//   misses: unreachable), mTLS hosts are not probed yet.
// The twin's own platform satellite carries Acme's two DEs, so the page has something to show.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, problem, validation } from "./fake";
import { COMPANY_IDS, DE_IDS, uid } from "./fake-data";
import type { MeResponse } from "./types";

type SatelliteView = OperationOutput<"operator.satellites.list">["data"][number];
type Satellite = Omit<SatelliteView, "endpoints" | "releases">;
type Release = SatelliteView["releases"][number];
type Endpoint = SatelliteView["endpoints"][number];

export const FAKE_SATELLITE_ROUTES = [
  "operator.satellites.list",
  "operator.satellites.create",
  "operator.satellites.update",
  "operator.satellites.retire",
  "operator.satellites.probe",
  "operator.agentEndpoints.create",
  "operator.agentEndpoints.bulk",
  "operator.agentEndpoints.probe",
  "operator.agentEndpoints.retire",
  "operator.systemAgents.endpoint",
] as const satisfies readonly RouteId[];

const inOverlay = (host: string) => {
  const m = /^100\.(\d{1,3})\.\d{1,3}\.\d{1,3}$/.exec(host);
  return Boolean(m && Number(m[1]) >= 64 && Number(m[1]) <= 127);
};

export function createSatellitesFake(deps: { now: () => Date }) {
  let seq = 0;
  const nextId = () => uid(0x5a7e, ++seq);
  const at = () => deps.now().toISOString();
  const created = deps.now().toISOString();
  const satellites: Satellite[] = [
    {
      id: nextId(),
      company_id: null,
      name: "Platform node 1",
      transport: "wireguard",
      base_address: "http://100.64.10.2",
      health_path: "/health",
      status: "live",
      max_concurrent_runs: 4,
      source: "panel",
      panel_node_id: "node-1",
      last_seen_at: created,
      last_probe_at: created,
      last_probe_detail: "answered 200",
      probe_misses: 0,
      retired_at: null,
      retired_reason: null,
      created_at: created,
    },
  ];
  const first = satellites[0] as Satellite;
  const endpoints: Endpoint[] = [
    ["riley", DE_IDS.riley],
    ["sam", DE_IDS.sam],
  ].map(([profile, de]) => ({
    id: nextId(),
    company_id: COMPANY_IDS.acme,
    de_membership_id: de as string,
    satellite_id: first.id,
    instance_id: "acme-hermes",
    profile_name: profile as string,
    api_base_url: "http://100.64.10.2:8642",
    multiplex: true,
    source: "panel",
    status: "active",
    created_at: created,
    probe: { at: created, ok: true, detail: "HTTP 404: the key was accepted", current: true },
  }));

  const refuse = (me: MeResponse): Response | null => {
    if (!me.agency) return notFound();
    const htva = me.agency.grants.some(
      (g) => g.company_id === COMPANY_IDS.htva && g.level === "operator",
    );
    if (me.agency.role !== "admin" || !htva)
      return problem(
        403,
        "forbidden",
        "Forbidden",
        "Satellites are managed by an admin of the platform's agency",
      );
    return null;
  };
  // SATELLITE-RUNTIME: what the panel reported on the first node (the bridge current, the render
  // service one release behind).
  const sha = (c: string) => c.repeat(64);
  const releasesOf = (s: Satellite): Release[] =>
    s.id !== first.id
      ? []
      : [
          {
            component: "bridge",
            pinned: { version: "2.0.0", sha256: sha("a") },
            installed: {
              version: "2.0.0",
              sha256: sha("a"),
              action: "installed",
              reported_at: created,
              detail: null,
            },
            drift: [],
            state: "current",
          },
          {
            component: "render_service",
            pinned: { version: "2.7.1", sha256: sha("b") },
            installed: {
              version: "2.7.0",
              sha256: sha("c"),
              action: "checked",
              reported_at: created,
              detail: null,
            },
            drift: ["palette_check.py"],
            state: "behind",
          },
        ];
  const view = (s: Satellite) => ({
    ...s,
    endpoints: endpoints.filter((e) => e.satellite_id === s.id && e.status === "active"),
    releases: releasesOf(s),
  });
  const body = (b: unknown) => (b && typeof b === "object" ? (b as Record<string, unknown>) : {});
  const str = (v: unknown) => (typeof v === "string" ? v.trim() : "");

  function checkAddress(transport: string, raw: string): string | { problem: string } {
    let u: URL;
    try {
      u = new URL(raw);
    } catch {
      return { problem: "base_address: not an absolute URL" };
    }
    if ((u.pathname !== "/" && u.pathname !== "") || u.search || u.hash || u.username)
      return { problem: "base_address: an origin only (scheme, host, port)" };
    if (transport === "mtls") {
      if (u.protocol !== "https:") return { problem: "base_address: mTLS needs https" };
      if (/^\d+\.\d+\.\d+\.\d+$/.test(u.hostname) || !u.hostname.includes("."))
        return { problem: "base_address: mTLS needs a public DNS name" };
    } else if (!inOverlay(u.hostname))
      return { problem: "base_address: outside the Hermes overlay (100.64.0.0/10)" };
    return u.origin;
  }

  const handlers: Partial<Record<RouteId, Handler>> = {
    "operator.satellites.list": ({ me }) => refuse(me) ?? json({ data: satellites.map(view) }),
    "operator.satellites.create": ({ me, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const b = body(raw);
      const name = str(b.name);
      const transport = b.transport === "mtls" ? "mtls" : "wireguard";
      if (!name) return validation("name: required");
      const address = checkAddress(transport, str(b.base_address));
      if (typeof address !== "string") return validation(address.problem);
      const companyId = typeof b.company_id === "string" ? b.company_id : null;
      if (companyId && !me.agency?.grants.some((g) => g.company_id === companyId))
        return notFound();
      const s: Satellite = {
        id: nextId(),
        company_id: companyId,
        name,
        transport,
        base_address: address,
        health_path: "/health",
        status: "live",
        max_concurrent_runs: typeof b.max_concurrent_runs === "number" ? b.max_concurrent_runs : 4,
        source: "manual",
        panel_node_id: null,
        last_seen_at: null,
        last_probe_at: null,
        last_probe_detail: null,
        probe_misses: 0,
        retired_at: null,
        retired_reason: null,
        created_at: at(),
      };
      satellites.push(s);
      return json(view(s), 201);
    },
    "operator.satellites.update": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const s = satellites.find((x) => x.id === params.id);
      if (!s || s.status === "retired") return notFound();
      const b = body(raw);
      if (typeof b.name === "string" && b.name.trim()) s.name = b.name.trim();
      if (typeof b.max_concurrent_runs === "number") s.max_concurrent_runs = b.max_concurrent_runs;
      if (b.draining === true) s.status = "draining";
      if (b.draining === false && s.status === "draining") s.status = "live";
      return json(view(s));
    },
    "operator.satellites.retire": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const s = satellites.find((x) => x.id === params.id);
      if (!s) return notFound();
      const already = s.status === "retired";
      let n = 0;
      if (!already) {
        s.status = "retired";
        s.retired_at = at();
        s.retired_reason = str(body(raw).reason) || "retired";
        for (const e of endpoints)
          if (e.satellite_id === s.id && e.status === "active") {
            e.status = "retired";
            n++;
          }
      }
      return json({ satellite: view(s), endpoints_retired: n, already });
    },
    "operator.satellites.probe": ({ me, params }) => {
      const no = refuse(me);
      if (no) return no;
      const s = satellites.find((x) => x.id === params.id);
      if (!s) return notFound();
      const previous = s.status;
      s.last_probe_at = at();
      if (s.transport === "mtls") {
        s.last_probe_detail = "mTLS satellites are not probed yet";
        return json({
          satellite_id: s.id,
          status: s.status,
          previous,
          seen: false,
          detail: s.last_probe_detail,
        });
      }
      const seen = !new URL(s.base_address).hostname.endsWith(".99");
      if (seen) {
        s.last_seen_at = at();
        s.probe_misses = 0;
        s.last_probe_detail = "answered 200";
        if (s.status === "unreachable") s.status = "live";
      } else {
        s.probe_misses += 1;
        s.last_probe_detail = "no answer";
        if (s.probe_misses >= 2 && s.status === "live") s.status = "unreachable";
      }
      return json({
        satellite_id: s.id,
        status: s.status,
        previous,
        seen,
        detail: s.last_probe_detail,
      });
    },
    "operator.agentEndpoints.create": ({ me, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const b = body(raw);
      const companyId = str(b.company_id);
      if (!me.agency?.grants.some((g) => g.company_id === companyId)) return notFound();
      const s = satellites.find((x) => x.id === b.satellite_id);
      if (!s || s.status === "retired" || (s.company_id && s.company_id !== companyId))
        return validation("satellite_id: not a satellite this company can use");
      const url = str(b.api_base_url).replace(/\/+$/, "");
      let host = "";
      try {
        host = new URL(url).hostname;
      } catch {
        return validation("api_base_url: not an absolute URL");
      }
      if (host !== new URL(s.base_address).hostname)
        return validation("api_base_url: not on the satellite's host");
      if (!/^https?:\/\/[^/?#@]+$/.test(url))
        return validation("api_base_url: an origin (scheme, host, port), no path");
      if (typeof b.api_key !== "string" || b.api_key.length < 16)
        return validation("api_key: at least 16 characters");
      const de = str(b.de_membership_id);
      for (const e of endpoints)
        if (e.company_id === companyId && e.de_membership_id === de && e.status === "active")
          e.status = "retired";
      const e: Endpoint = {
        id: nextId(),
        company_id: companyId,
        de_membership_id: de,
        satellite_id: s.id,
        instance_id: str(b.instance_id),
        profile_name: str(b.profile_name),
        api_base_url: url,
        multiplex: b.multiplex !== false,
        source: "manual",
        status: "active",
        created_at: at(),
        probe: null,
      };
      endpoints.push(e);
      return json(e, 201);
    },
    "operator.agentEndpoints.bulk": ({ me, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const b = body(raw);
      const companyId = str(b.company_id);
      if (!me.agency?.grants.some((g) => g.company_id === companyId)) return notFound();
      const items = Array.isArray(b.endpoints) ? (b.endpoints as Record<string, unknown>[]) : [];
      const results = items.map((i) => ({
        profile_name: str(i.profile_name),
        de_membership_id: typeof i.de_membership_id === "string" ? i.de_membership_id : null,
        endpoint_id: null,
        outcome: (b.dry_run === true ? "would_create" : "created") as "created" | "would_create",
        problem: null,
      }));
      return json({
        dry_run: b.dry_run === true,
        created: results.length,
        unchanged: 0,
        failed: 0,
        results,
      });
    },
    "operator.agentEndpoints.probe": ({ me, params }) => {
      const no = refuse(me);
      if (no) return no;
      const e = endpoints.find((x) => x.id === params.id && x.status === "active");
      if (!e) return notFound();
      e.probe = { at: at(), ok: true, detail: "HTTP 404: the key was accepted", current: true };
      return json({
        endpoint_id: e.id,
        profile_name: e.profile_name,
        ok: true,
        detail: e.probe.detail,
      });
    },
    "operator.agentEndpoints.retire": ({ me, params }) => {
      const no = refuse(me);
      if (no) return no;
      const e = endpoints.find((x) => x.id === params.id);
      if (!e) return notFound();
      e.status = "retired";
      return json(e);
    },
    "operator.systemAgents.endpoint": ({ me, params }) => {
      const no = refuse(me);
      if (no) return no;
      const e = endpoints.find(
        (x) =>
          x.company_id === params.company_id &&
          x.de_membership_id === params.membership_id &&
          x.status === "active",
      );
      if (!e) return notFound();
      const base = e.multiplex ? `${e.api_base_url}/p/${e.profile_name}` : e.api_base_url;
      return json({ api_base_url: base, api_key: null, allow_plain_http: true });
    },
  };
  /** OPERATOR-CONSOLE reads where DEs run (the roster, the baseline's containers). */
  const placement = () => ({
    satellites: satellites.filter((s) => !s.retired_at).map((s) => ({ id: s.id, name: s.name })),
    endpoints: endpoints
      .filter((e) => e.status === "active")
      .map((e) => ({
        company_id: e.company_id,
        de_membership_id: e.de_membership_id,
        satellite_id: e.satellite_id,
        container: e.instance_id,
        profile: e.profile_name,
      })),
  });
  return { handlers, placement };
}
